import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Badge, Select, Button } from '@components';
import { UsageRecord } from '../types';

interface UsageRecordListProps {
  records: UsageRecord[];
  loading?: boolean;
  onDateRangeChange?: (range: string) => void;
  /**
   * Server-computed total across the full (unpaginated) result set.
   * When provided, it is shown in the footer instead of summing the
   * paginated `records`, which would otherwise only reflect the first page.
   */
  totalCost?: string;
  pagination?: {
    count: number;
    next: string | null;
    previous: string | null;
    onNext?: () => void;
    onPrevious?: () => void;
  };
}

const USAGE_TYPE_CONFIG: Record<
  UsageRecord['usage_type'],
  { labelKey: string; icon: string; variant: 'info' | 'success' | 'warning' | 'error' }
> = {
  call_minutes: { labelKey: 'billing.callMinutes', icon: 'phone', variant: 'info' },
  sms: { labelKey: 'billing.sms', icon: 'sms', variant: 'success' },
  web_search: { labelKey: 'billing.webSearch', icon: 'search', variant: 'warning' },
  phone_number: { labelKey: 'billing.phoneNumbers', icon: 'dialpad', variant: 'error' },
  base_fee: { labelKey: 'billing.monthlyBaseFee', icon: 'receipt_long', variant: 'info' },
};

export const UsageRecordList: React.FC<UsageRecordListProps> = ({
  records,
  loading = false,
  onDateRangeChange,
  totalCost,
  pagination,
}) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState('current');

  const handleDateRangeChange = (value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value[0] : value;
    setDateRange(stringValue);
    onDateRangeChange?.(stringValue);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCost = (cost: string) => `€${parseFloat(cost).toFixed(2)}`;

  const formatQuantity = (record: UsageRecord) => {
    if (record.usage_type === 'call_minutes') {
      const mins = Math.floor(record.quantity / 60);
      const secs = record.quantity % 60;
      return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    }
    return String(record.quantity);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('billing.usageHistory')}
        </h2>
        <Select
          value={dateRange}
          onChange={handleDateRangeChange}
          options={[
            { value: 'all', label: t('billing.allTime') },
            { value: 'current', label: t('billing.currentPeriod') },
            { value: 'last', label: t('billing.lastMonth') },
            { value: 'quarter', label: t('billing.thisQuarter') },
            { value: 'year', label: t('billing.thisYear') },
          ]}
          className="w-52"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <span className="material-icons animate-spin text-4xl mb-2 text-gray-400">
                refresh
              </span>
              <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
            </div>
          ) : records.length === 0 ? (
            <div className="p-8 text-center">
              <span className="material-icons text-4xl text-gray-300 dark:text-gray-600 mb-2">
                receipt_long
              </span>
              <p className="text-gray-500 dark:text-gray-400">{t('billing.noUsageData')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                      {t('billing.date')}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                      {t('billing.usageType')}
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                      {t('billing.quantity')}
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                      {t('billing.unitPrice')}
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                      {t('billing.amount')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {records.map(record => {
                    const config = USAGE_TYPE_CONFIG[record.usage_type] ?? {
                      labelKey: record.usage_type,
                      icon: 'receipt',
                      variant: 'info' as const,
                    };
                    return (
                      <tr
                        key={record.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {formatDate(record.created)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="material-icons text-base text-gray-400">
                              {config.icon}
                            </span>
                            <Badge variant={config.variant} size="sm">
                              {t(config.labelKey)}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                          {formatQuantity(record)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">
                          {formatCost(record.unit_price)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                          {formatCost(record.total_cost)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <td
                      colSpan={4}
                      className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300"
                    >
                      {t('billing.total')}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">
                      {formatCost(
                        totalCost ??
                          records
                            .reduce((sum, r) => sum + parseFloat(r.total_cost || '0'), 0)
                            .toFixed(2)
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Pagination - only shown when there are multiple pages */}
          {!loading && pagination && (pagination.next || pagination.previous) && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t('billing.showingRecords', {
                  shown: records.length,
                  total: pagination.count,
                })}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={pagination.onPrevious}
                  disabled={!pagination.previous || loading}
                >
                  {t('common.previous')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={pagination.onNext}
                  disabled={!pagination.next || loading}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
